export type SharedMapCategory = "airport" | "port" | "hotel" | "place" | "experience" | "food" | "event";
type Row = Record<string, unknown>;
export type SharedMapPhoto = { url: string; caption: string; album: string; date: string; sourceId: string };
export type SharedMapPoint = { id: string; sourceId: string; title: string; category: SharedMapCategory; date: string; endDate: string; time: string; notes: string; address: string; position: [number, number] | null; photos: SharedMapPhoto[] };
export type SharedMapRoute = { id: string; from: [number, number]; to: [number, number]; date: string; endDate: string; title: string; kind: "flight" | "ferry" | "land" };
const text = (value: unknown) => typeof value === "string" ? value.trim() : "";
function rows(value: unknown): Row[] { return Array.isArray(value) ? value.filter((x): x is Row => !!x && typeof x === "object" && !Array.isArray(x)) : []; }
export function sharedPosition(lat: unknown, lon: unknown): [number, number] | null {
  if (typeof lat !== "number" || typeof lon !== "number" || !Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return [lat, lon];
}
export function sharedPhotoUrl(value: unknown): string {
  const url = text(value);
  return /^https:\/\//i.test(url) || /^data:image\/(png|jpe?g|webp|gif);base64,[a-z0-9+/=]+$/i.test(url) ? url : "";
}
export function buildSharedMapModel(snapshot: Record<string, unknown>) {
  const photos: SharedMapPhoto[] = rows(snapshot.photos).flatMap(p => {
    const url = sharedPhotoUrl(p.url);
    return url ? [{ url, caption: text(p.caption || p.name), album: text(p.folder || p.city), date: text(p.date), sourceId: text(p.sourceId) }] : [];
  });
  const points: SharedMapPoint[] = [];
  const routes: SharedMapRoute[] = [];
  const sources: Array<[string, SharedMapCategory]> = [["boardingPasses","airport"],["itinerary","place"],["places","place"],["pointsOfInterest","place"],["hotels","hotel"],["restaurants","food"],["experiences","experience"],["events","event"]];
  for (const [section, fallback] of sources) for (const [index, row] of rows(snapshot[section]).entries()) {
    const flight = section === "boardingPasses";
    const mode = flight ? "flight" : text(row.transportModeId).toLowerCase();
    const transfer = flight || (section === "itinerary" && row.type === "transfer");
    const kind = /flight|plane|air|volo/.test(mode) ? "flight" : /ferry|boat|ship|traghetto/.test(mode) ? "ferry" : "land";
    const category = transfer ? kind === "flight" ? "airport" : kind === "ferry" ? "port" : "place" : fallback;
    const sourceId = text(row.id);
    const id = section + ":" + (sourceId || index);
    const date = text(row.date || row.visitDate || row.startDate || row.checkIn || row.departureDate);
    const endDate = text(row.endDate || row.checkOut) || date;
    const position = sharedPosition(row.latitude, row.longitude);
    const ownPhoto = sharedPhotoUrl(row.customPhotoDataUrl);
    const linked = photos.filter(p => !!sourceId && p.sourceId === sourceId);
    const point: SharedMapPoint = { id, sourceId, title: flight ? [text(row.originCity || row.originCode), text(row.destinationCity || row.destinationCode)].filter(Boolean).join(" – ") : text(row.title || row.name || row.placeName || row.city), category, date, endDate, time: text(row.startTime || row.time || row.departureTime), notes: Object.hasOwn(snapshot, "notes") ? text(row.notes || row.description) : "", address: text(row.address || row.city || row.originCity || row.originCode), position, photos: ownPhoto ? [{ url: ownPhoto, caption: text(row.name), album: text(row.city), date, sourceId }, ...linked] : linked };
    points.push(point);
    const end = transfer ? sharedPosition(row.endLatitude, row.endLongitude) : null;
    if (end) {
      points.push({ ...point, id: id + ":end", title: text(row.endCity || row.endAddress || row.destinationCity || row.destinationCode) || point.title, address: text(row.endAddress || row.endCity || row.destinationCity || row.destinationCode), position: end });
      if (position) routes.push({ id, from: position, to: end, date, endDate, title: point.title, kind });
    }
  }
  return { points: points.sort((a,b) => (a.date + a.time).localeCompare(b.date + b.time)), routes, photos };
}
export function sharedDayMatches(item: {date: string; endDate?: string}, day: string) {
  return day === "all" || (!!item.date && item.date <= day && (item.endDate || item.date) >= day);
}
export function sharedMapDays(start: string, end: string, points: SharedMapPoint[]): string[] {
  const valid = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value + "T12:00:00Z"));
  const found = new Set(points.flatMap(p => [p.date, p.endDate]).filter(valid));
  if (valid(start) && valid(end)) {
    const count = Math.round((Date.parse(end + "T12:00:00Z") - Date.parse(start + "T12:00:00Z")) / 86400000);
    if (count >= 0 && count <= 730) for (let i=0; i<=count; i++) found.add(new Date(Date.parse(start + "T12:00:00Z") + i*86400000).toISOString().slice(0,10));
  }
  return [...found].sort();
}

/** Copy only validated, current coordinates already resolved by the existing trip map.
 * The input is the visibility-filtered share snapshot; never read additional trip sections. */
export function withSharedMapCoordinates(snapshot: Record<string, unknown>, rawCache: unknown): Record<string, unknown> {
  if (!rawCache || typeof rawCache !== "object" || Array.isArray(rawCache)) return snapshot;
  const cache = Object.entries(rawCache as Record<string, unknown>);
  const next = { ...snapshot };
  const sources: Record<string,string> = { itinerary:"manual", places:"city", pointsOfInterest:"poi", hotels:"hotel", restaurants:"restaurant", experiences:"experience", events:"event", boardingPasses:"boardingPass" };
  for (const [section, kind] of Object.entries(sources)) {
    if (!Array.isArray(snapshot[section])) continue;
    next[section] = rows(snapshot[section]).map(row => {
      const copy = { ...row }; const id = text(row.id); if (!id) return copy;
      const flight = section === "boardingPasses";
      const query = flight ? text(row.originCity || row.originCode) : text(row.address || row.city || row.title || row.name || row.placeName);
      const endQuery = flight ? text(row.destinationCity || row.destinationCode) : text(row.endAddress || row.endCity);
      for (const [endpoint, expected, latKey, lonKey] of [["start",query,"latitude","longitude"],["end",endQuery,"endLatitude","endLongitude"]]) {
        if (!expected || sharedPosition(copy[latKey],copy[lonKey])) continue;
        const found = cache.find(([key,value]) => {
          if (!(key === kind+":"+id+":"+endpoint || (key.startsWith(kind+":"+id+":") && key.endsWith(":"+endpoint)))) return false;
          if (!value || typeof value !== "object") return false;
          const entry = value as Row;
          return entry.query === expected && !!sharedPosition(entry.latitude,entry.longitude);
        });
        if (found) { const entry=found[1] as Row; copy[latKey]=entry.latitude;copy[lonKey]=entry.longitude; }
      }
      return copy;
    });
  }
  return next;
}
